import { Component } from '@theme/component';
import {
  CartAddEvent,
  CartUpdateEvent,
  DiscountUpdateEvent,
  QuantitySelectorUpdateEvent,
  ThemeEvents,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { debounce, fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';

/**
 * Whether a `/cart/change` error means the row's line is no longer in the cart.
 * @param {{ errors?: unknown }} response
 * @returns {boolean}
 */
const isStaleLineError = (response) => /no valid id or line/i.test(String(response?.errors ?? ''));

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleSubscribeSaveClick);
    this.addEventListener('change', this.#handleSubscriptionChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleSubscribeSaveClick);
    this.removeEventListener('change', this.#handleSubscriptionChange);
  }

  /**
   * Handles the Subscribe & Save button click.
   * @param {Event} event - The event.
   */
  #handleSubscribeSaveClick = (event) => {
    try {
      if (!(event.target instanceof HTMLElement)) return;
      const button = event.target.closest('.js-subscribe-save');
      if (!button) return;

      const wrapper = button.closest('.cart-items__subscribe-wrapper');
      const selectContainer = wrapper?.querySelector('.sl-form__field--select');
      const select = wrapper?.querySelector('.js-subscription-select');
      if (!(selectContainer instanceof HTMLElement) || !(button instanceof HTMLElement) || !(select instanceof HTMLSelectElement)) return;

      // Get the first subscription option (skip the onetime option)
      const subscriptionOptions = select.querySelectorAll('optgroup[label*="Subscribe"] option');
      if (subscriptionOptions.length === 0) return;

      const firstSubscriptionPlan = /** @type {HTMLOptionElement} */ (subscriptionOptions[0]).value;
      select.value = firstSubscriptionPlan;

      // Show select and hide button
      // selectContainer.hidden = false;
      // button.hidden = true;
      button.textContent = 'Upgrading...';
      button.disabled = true;

      // Trigger subscription update with first plan
      const itemKey = button.dataset.itemKey;
      const quantity = button.dataset.quantity || '1';

      if (!itemKey) return;

      this.updateSubscription({
        id: itemKey,
        quantity,
        selling_plan: firstSubscriptionPlan,
      });
    } catch (error) {
      console.error('CartItemsComponent.#handleSubscribeSaveClick failed:', error);
    }
  };

  /**
   * Handles the subscription select change.
   * @param {Event} event - The event.
   */
  #handleSubscriptionChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const select = event.target.closest('.js-subscription-select');
    if (!(select instanceof HTMLSelectElement)) return;

    const wrapper = select.closest('.cart-items__subscribe-wrapper');
    const button = wrapper?.querySelector('.js-subscribe-save');
    const selectContainer = wrapper?.querySelector('.sl-form__field--select');
    if (!(button instanceof HTMLElement) || !(selectContainer instanceof HTMLElement)) return;

    const itemKey = button.dataset.itemKey;
    const quantity = button.dataset.quantity || '1';
    const sellingPlanId = select.value;

    if (!itemKey) return;

    // If 'onetime' is selected, show button and hide select
    if (sellingPlanId === 'onetime') {
      selectContainer.hidden = true;
      button.hidden = false;
      button.disabled = true;
    }

    // Update cart with selected plan (empty string for onetime)
    this.updateSubscription({
      id: itemKey,
      quantity,
      selling_plan: sellingPlanId === 'onetime' ? '' : sellingPlanId,
    });
  };

  /**
   * Updates the item subscription.
   * @param {Object} config - The config.
   * @param {string} config.id - The item key.
   * @param {string} config.quantity - The quantity.
   * @param {string} config.selling_plan - The selling plan id.
   */
  updateSubscription(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('update-subscription:user-action');

    this.#disableCartItems();

    const { id, quantity, selling_plan } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: id,
      quantity: quantity,
      selling_plan: selling_plan,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // You might want to handle subscription errors differently, 
          // but for now, we'll try to find the line number if possible.
          // Since we have the item key, we could potentially find the row index.
          const rowIndex = this.refs.cartItemRows.findIndex(row => row.dataset.key === id) + 1;
          if (rowIndex > 0) {
            this.#handleCartError(rowIndex, parsedResponseText);
          }
          return;
        }

        if (!parsedResponseText.sections) return this.#resyncAfterRejectedEdit();

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    try {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });

      const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

      if (!cartItemRowToRemove) return;

      const rowsToRemove = [
        cartItemRowToRemove,
        // Get all nested lines of the row to remove
        ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ];

      // Add class to the row to trigger the animation
      rowsToRemove.forEach((row) => {
        const remove = () => row.remove();

        if (prefersReducedMotion()) return remove();

        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');

        // Remove the row after the animation ends
        onAnimationEnd(row, remove);
      });
    } catch (error) {
      console.error('CartItemsComponent.onLineItemRemove failed:', error);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Address the line by its KEY when the row has one, not by its position. A drawer rendered before
    // the cart changed (a page served from the Back/Forward cache, an app's write) still numbers its
    // rows the old way, and `line: 1` then removed whatever the server now holds first — measured live
    // 2026-10-15: "Remove" on the AirTouch F30i row deleted the N30i added on another page. A key that
    // is no longer in the cart answers with an error instead of changing the wrong product.
    const lineKey = this.refs.cartItemRows?.[line - 1]?.dataset.key;
    const body = JSON.stringify({
      ...(lineKey ? { id: lineKey } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // The row's line is no longer in the cart (a drawer rendered before the cart changed): on
          // Remove the row is already gone, and the rest of the drawer is out of date. Redraw it
          // instead of writing a line error into a row that no longer exists.
          if (isStaleLineError(parsedResponseText)) return this.#resyncAfterRejectedEdit();
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        if (!parsedResponseText.sections) return this.#resyncAfterRejectedEdit();

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  /**
   * Redraws the drawer from the server after an edit the cart refused without an `errors` payload.
   *
   * A line key that is no longer in the cart — a row from a drawer rendered before the cart changed —
   * answers 400 `{ errors: "no valid id or line parameter" }` (measured 2026-10-15), and any other
   * body without `sections` cannot be morphed either. The row the shopper clicked has already been
   * animated away, so without this the rest of the drawer and the bubble stayed stale. `background`
   * keeps the drawer's auto-open out of it; the cart icon has no count in the event, so it asks the
   * cart.
   */
  #resyncAfterRejectedEdit() {
    sectionRenderer.renderSection(this.sectionId, { cache: false }).catch((error) => {
      console.error('Cart drawer re-render after a rejected edit failed:', error);
    });
    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, { source: 'cart-items-component', background: true })
    );
  }

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
